import { useState, useEffect, useCallback } from 'react';
import type { TourStep } from '@/components/ui/product-tour';

const TOUR_STORAGE_KEY = 'assets-tour-completed';

export function useAssetsTour() {
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [hasCompletedTour, setHasCompletedTour] = useState(false);

  useEffect(() => {
    const completed = localStorage.getItem(TOUR_STORAGE_KEY);
    setHasCompletedTour(completed === 'true');
  }, []);

  const startTour = useCallback(() => {
    setIsTourOpen(true);
  }, []);

  const closeTour = useCallback(() => {
    setIsTourOpen(false);
  }, []);

  const completeTour = useCallback(() => {
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    setHasCompletedTour(true);
    setIsTourOpen(false);
  }, []);

  const resetTour = useCallback(() => {
    localStorage.removeItem(TOUR_STORAGE_KEY);
    setHasCompletedTour(false);
  }, []);

  const tourSteps: TourStep[] = [
    {
      target: '[data-tour="assets-header"]',
      title: 'Welcome to Assets',
      content: 'This is your central hub for managing all your assets. Here you can view, search, filter, and perform various operations on your asset inventory.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="search-bar"]',
      title: 'Search Assets',
      content: 'Quickly find assets by searching for their name, code, serial number, or description. The search is instant and filters results as you type.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="sort-dropdown"]',
      title: 'Sort Assets',
      content: 'Organize your assets by category, name, date, or value. Choose ascending or descending order to view your assets the way you prefer.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="view-toggle"]',
      title: 'View Modes',
      content: 'Switch between Gallery view for a visual overview and List view for detailed tabular information. Choose the view that works best for your workflow.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="grid-size"]',
      title: 'Adjust Grid Size',
      content: 'In Gallery view, customize how many columns of assets you want to see. Choose between 4, 5, or 6 columns to fit your screen size.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="new-asset-button"]',
      title: 'Create New Asset',
      content: 'Click here to add a new asset to your inventory. You\'ll be guided through entering all the necessary details.',
      placement: 'left',
    },
    {
      target: '[data-tour="asset-selection"]',
      title: 'Select Assets',
      content: 'Use the checkboxes to select one or more assets. Selecting assets unlocks bulk actions like creating transactions, exporting data, or printing labels.',
      placement: 'right',
    },
    {
      target: '[data-tour="right-panel"]',
      title: 'Actions & Filters Panel',
      content: 'This panel provides quick access to actions, filters, and column customization. Select assets to see available bulk actions, or use filters to narrow down your view.',
      placement: 'left',
    },
  ];

  return {
    isTourOpen,
    hasCompletedTour,
    tourSteps,
    startTour,
    closeTour,
    completeTour,
    resetTour,
  };
}
